import { api } from "@/modules/api";
import { toastModule } from "@/modules/toast";
import styled from "@emotion/styled";
import { Button, Checkbox, Dialog, Flex, Text, TextField, Tooltip } from "@radix-ui/themes";
import { FC, useMemo, useState } from "react";

type ResetOptions = {
  resetAccountPlatformOnboardingInfo: boolean;
  resetHasInteractedWithV2Platform: boolean;
  resetHasOnboardedWithV2Platform: boolean;
  cancelNewAccountOnboardingWorkflow: boolean;
  cancelNewV2AccountOnboardingWorkflow: boolean;
};

type ResetLockedOn = Partial<Record<keyof ResetOptions, boolean>>;

const DEFAULT_RESET_OPTIONS: ResetOptions = {
  resetAccountPlatformOnboardingInfo: true,
  resetHasInteractedWithV2Platform: true,
  resetHasOnboardedWithV2Platform: true,
  cancelNewAccountOnboardingWorkflow: true,
  cancelNewV2AccountOnboardingWorkflow: true,
};

export const AccountResetDialog: FC<{
  accountId: string;
  primarySpaceAccountId?: string | null;
  onAfterReset?: () => Promise<void> | void;
}> = ({ accountId, primarySpaceAccountId, onAfterReset }) => {
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resetOptions, setResetOptions] = useState<ResetOptions>(DEFAULT_RESET_OPTIONS);
  const [isResetting, setIsResetting] = useState(false);
  const [lockedOn, setLockedOn] = useState<ResetLockedOn>({});
  const [isLoadingResetState, setIsLoadingResetState] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetConfirmError, setResetConfirmError] = useState<string | null>(null);

  const canOpen = Boolean(primarySpaceAccountId);

  const isResetOptionLockedOn = (key: keyof ResetOptions) => lockedOn[key] === true;

  const allResetOptionsSelected = (Object.keys(resetOptions) as (keyof ResetOptions)[]).every(
    key => resetOptions[key] === true
  );

  const setAllResetOptionsSelected = (value: boolean) => {
    setResetOptions(prev => {
      const next: ResetOptions = { ...prev };
      (Object.keys(next) as (keyof ResetOptions)[]).forEach(key => {
        if (isResetOptionLockedOn(key)) {
          next[key] = true;
          return;
        }
        next[key] = value;
      });
      return next;
    });
  };

  const disabledReason = useMemo(() => {
    if (primarySpaceAccountId) return null;
    return "No primary space account found for this account.";
  }, [primarySpaceAccountId]);

  const openResetDialog = () => {
    if (!primarySpaceAccountId) return;

    setLockedOn({});
    setIsLoadingResetState(true);
    setResetOptions(DEFAULT_RESET_OPTIONS);
    setResetConfirmText("");
    setResetConfirmError(null);
    setIsResetDialogOpen(true);

    api
      .get("/internal/v1/space-accounts/{space_account_id}/reset-state", {
        params: { path: { space_account_id: primarySpaceAccountId } },
      })
      .then(response => {
        const data = response.data;
        if (!data) return;

        const nextLockedOn: ResetLockedOn = {
          resetAccountPlatformOnboardingInfo: data.account_platform_onboarding_is_active,
          resetHasInteractedWithV2Platform: data.has_interacted_with_v2_platform_is_active,
          resetHasOnboardedWithV2Platform: data.has_onboarded_with_v2_platform_is_active,
        };

        setLockedOn(nextLockedOn);
        setResetOptions(prev => {
          const next: ResetOptions = { ...prev };
          (Object.keys(nextLockedOn) as (keyof ResetOptions)[]).forEach(key => {
            if (nextLockedOn[key]) {
              next[key] = true;
            }
          });
          return next;
        });
      })
      .catch(err => {
        console.error("Failed to fetch reset state:", err);
      })
      .finally(() => {
        setIsLoadingResetState(false);
      });
  };

  const resetConfirmTextIsValid = resetConfirmText.trim().toUpperCase() === "CONFIRM";

  const handleRunReset = async () => {
    if (!primarySpaceAccountId) return;

    setResetConfirmError(null);
    if (!resetConfirmTextIsValid) {
      setResetConfirmError("Type CONFIRM to proceed.");
      return;
    }

    setIsResetting(true);
    try {
      const response = await api.post("/internal/v1/space-accounts/{space_account_id}/reset", {
        params: { path: { space_account_id: primarySpaceAccountId } },
        body: {
          // Space account (intentionally excluded from this dialog)
          reset_import_cta: false,
          reset_icp_rfi: false,
          reset_heads_up_coach_mark: false,
          reset_onboarding_checklist: false,
          reset_app_callouts: false,
          reset_space_account_settings: false,

          // Account
          reset_account_platform_onboarding_info: resetOptions.resetAccountPlatformOnboardingInfo,
          reset_has_interacted_with_v2_platform: resetOptions.resetHasInteractedWithV2Platform,
          reset_has_onboarded_with_v2_platform: resetOptions.resetHasOnboardedWithV2Platform,
          cancel_new_account_onboarding_workflow: resetOptions.cancelNewAccountOnboardingWorkflow,
          cancel_new_v2_account_onboarding_workflow:
            resetOptions.cancelNewV2AccountOnboardingWorkflow,
        },
      });

      if (!response.data?.success) {
        toastModule.triggerToast({
          content: "Reset completed with errors. See console for details (and response payload).",
        });
        console.error("Reset completed with errors:", response.data);
      } else {
        toastModule.triggerToast({ content: "Reset completed successfully." });
      }

      await onAfterReset?.();
      setIsResetDialogOpen(false);
    } catch (error) {
      toastModule.triggerToast({ content: "Failed to reset. Check console for details." });
      console.error("Failed to reset account:", error);
    } finally {
      setIsResetting(false);
    }
  };

  const trigger = (
    <span style={{ display: "inline-flex" }}>
      <Button variant="outline" onClick={openResetDialog} disabled={!canOpen}>
        Reset…
      </Button>
    </span>
  );

  return (
    <>
      {disabledReason ? <Tooltip content={disabledReason}>{trigger}</Tooltip> : trigger}
      <Dialog.Root open={isResetDialogOpen} onOpenChange={open => setIsResetDialogOpen(open)}>
        <Dialog.Content style={{ maxWidth: "720px" }}>
          <Dialog.Title>Reset account onboarding + flags</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Select which reset actions to run. This is intended for internal testing and will be
            recorded for auditing.
          </Dialog.Description>

          <WarningBox>
            <Text weight="bold">Warning</Text>
            <Text size="2" color="gray">
              Avoid using this for live users. This can change onboarding state and cancel
              onboarding workflows.
            </Text>
            <Text size="2" color="gray">
              Account: <b>{accountId}</b>
            </Text>
          </WarningBox>

          {isLoadingResetState ? (
            <Text size="2" color="gray">
              Loading current onboarding state…
            </Text>
          ) : null}

          <SectionHeading size="2" mb="2">
            Select all (default)
          </SectionHeading>
          <CheckboxRow>
            <Checkbox
              checked={allResetOptionsSelected}
              onCheckedChange={value => setAllResetOptionsSelected(value === true)}
              id="account-reset-select-all"
            />
            <Text as="label" htmlFor="account-reset-select-all">
              Reset everything
            </Text>
          </CheckboxRow>

          <SectionHeading size="2" mb="2" mt="4">
            Account
          </SectionHeading>
          <OptionsGrid>
            <CheckboxRow>
              <Checkbox
                checked={resetOptions.resetAccountPlatformOnboardingInfo}
                onCheckedChange={value =>
                  setResetOptions(prev => ({
                    ...prev,
                    resetAccountPlatformOnboardingInfo: value === true,
                  }))
                }
                disabled={isResetOptionLockedOn("resetAccountPlatformOnboardingInfo")}
                id="account-reset-platform-onboarding-info"
              />
              <Text as="label" htmlFor="account-reset-platform-onboarding-info">
                Platform onboarding info (profile/completion)
              </Text>
            </CheckboxRow>

            <CheckboxRow>
              <Checkbox
                checked={resetOptions.resetHasInteractedWithV2Platform}
                onCheckedChange={value =>
                  setResetOptions(prev => ({
                    ...prev,
                    resetHasInteractedWithV2Platform: value === true,
                  }))
                }
                disabled={isResetOptionLockedOn("resetHasInteractedWithV2Platform")}
                id="account-reset-has-interacted-with-v2"
              />
              <Text as="label" htmlFor="account-reset-has-interacted-with-v2">
                has_interacted_with_v2_platform (resets on login)
              </Text>
            </CheckboxRow>

            <CheckboxRow>
              <Checkbox
                checked={resetOptions.resetHasOnboardedWithV2Platform}
                onCheckedChange={value =>
                  setResetOptions(prev => ({
                    ...prev,
                    resetHasOnboardedWithV2Platform: value === true,
                  }))
                }
                disabled={isResetOptionLockedOn("resetHasOnboardedWithV2Platform")}
                id="account-reset-has-onboarded-with-v2"
              />
              <Text as="label" htmlFor="account-reset-has-onboarded-with-v2">
                has_onboarded_with_v2_platform (resets on login)
              </Text>
            </CheckboxRow>

            <CheckboxRow>
              <Checkbox
                checked={resetOptions.cancelNewAccountOnboardingWorkflow}
                onCheckedChange={value =>
                  setResetOptions(prev => ({
                    ...prev,
                    cancelNewAccountOnboardingWorkflow: value === true,
                  }))
                }
                id="account-cancel-new-account-onboarding-workflow"
              />
              <LabelWithHelp>
                <Text as="label" htmlFor="account-cancel-new-account-onboarding-workflow">
                  Cancel classic onboarding workflow (Temporal)
                </Text>
                <Tooltip content="Cancels the short-lived onboarding kickoff workflow that decides onboarding variant and may trigger the V2 email workflow.">
                  <InfoButton type="button" aria-label="What does this cancel?">
                    ?
                  </InfoButton>
                </Tooltip>
              </LabelWithHelp>
            </CheckboxRow>

            <CheckboxRow>
              <Checkbox
                checked={resetOptions.cancelNewV2AccountOnboardingWorkflow}
                onCheckedChange={value =>
                  setResetOptions(prev => ({
                    ...prev,
                    cancelNewV2AccountOnboardingWorkflow: value === true,
                  }))
                }
                id="account-cancel-new-v2-account-onboarding-workflow"
              />
              <LabelWithHelp>
                <Text as="label" htmlFor="account-cancel-new-v2-account-onboarding-workflow">
                  Cancel v2 onboarding workflow (Temporal)
                </Text>
                <Tooltip content="Cancels the V2 onboarding email sequence (welcome email, optional day‑1 experiment email, and 48‑hour follow‑up).">
                  <InfoButton type="button" aria-label="What does this cancel?">
                    ?
                  </InfoButton>
                </Tooltip>
              </LabelWithHelp>
            </CheckboxRow>
          </OptionsGrid>

          <SectionHeading size="2" mb="2" mt="4">
            Confirmation
          </SectionHeading>
          <Text size="2" color="gray">
            Type <b>CONFIRM</b> to run the selected resets.
          </Text>
          <TextField.Root
            value={resetConfirmText}
            onChange={event => {
              setResetConfirmText(event.currentTarget.value);
              if (resetConfirmError) setResetConfirmError(null);
            }}
            onKeyDown={event => {
              if (event.key !== "Enter") return;
              event.preventDefault();

              if (isResetting || isLoadingResetState) return;
              if (!resetConfirmTextIsValid) return;

              void handleRunReset();
            }}
            placeholder="Type CONFIRM to acknowledge the warning"
            mt="2"
          />
          {resetConfirmError ? <ErrorText size="2">{resetConfirmError}</ErrorText> : null}

          <DialogActions>
            <Flex gap="2" justify="end">
              <Button
                variant="outline"
                onClick={() => setIsResetDialogOpen(false)}
                disabled={isResetting || isLoadingResetState}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRunReset}
                disabled={isResetting || isLoadingResetState || !resetConfirmTextIsValid}
                color="gray"
              >
                {isLoadingResetState ? "Loading…" : isResetting ? "Resetting…" : "Run reset"}
              </Button>
            </Flex>
          </DialogActions>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
};

const CheckboxRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const LabelWithHelp = styled(Flex)`
  align-items: center;
  gap: 6px;
`;

const InfoButton = styled.button`
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 999px;
  border: 1px solid var(--gray-7);
  background: var(--gray-2);
  color: var(--gray-11);
  font-size: 12px;
  line-height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;

  &:hover {
    background: var(--gray-3);
  }

  &:focus-visible {
    outline: 2px solid var(--focus-8);
    outline-offset: 2px;
  }
`;

const SectionHeading = styled(Text)`
  display: block;
  font-weight: 600;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
`;

const DialogActions = styled.div`
  margin-top: 20px;
`;

const WarningBox = styled.div`
  border: 1px solid var(--gray-6);
  background: var(--gray-2);
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ErrorText = styled(Text)`
  color: var(--red-11);
  display: block;
  margin-top: 6px;
`;
